import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    uploadBackup,
    clearUploadData,
    notifyDBUpdate
} from "../../Redux/Slice/DbExeUpdate";
import { fetchAllClients } from "../../Redux/WhatsappSlice/Client";
import {
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Checkbox,
    CircularProgress,
    Box,
    IconButton,
} from "@mui/material";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../Components/Loader/Loader";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const DbUpdate = () => {
    const dispatch = useDispatch();

    // Get all clients from Client slice
    const { AllClients, loading: clientsLoading } = useSelector(
        (state) => state.Client
    );

    const {
        uploadBackupLoading,
    } = useSelector((state) => state.DbExeUpdate);

    // New state for notifyDBUpdate loading
    const { loading: notifyDBUpdateLoading } = useSelector(
        (state) => state.UserSlice || { loading: false }
    );

    const [formData, setFormData] = useState({
        backupFiles: null,
    });

    const [selectedClientsForDBUpdate, setSelectedClientsForDBUpdate] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [openSelect, setOpenSelect] = useState(false); // State for select dropdown open/close

    // Process all clients for DB Update dropdown
    const allClientsList = AllClients?.data?.map((client) => ({
        id: String(client._id || client.id || ""),
        name: client.WhaClientName || client.name || client.clientName || "Unnamed Client",
    })) || [];

    const menuProps = {
        PaperProps: {
            style: {
                maxHeight: 300,
                width: 350,
            },
        },
        disablePortal: true,
    };

    const renderClientValue = (selected) => {
        if (!selected || selected.length === 0) {
            return "Select clients";
        }

        const selectedCount = selected.length;
        if (selectedCount > 3) {
            return `${selectedCount} clients selected`;
        }

        const names = selected
            .map((id) => {
                const client = allClientsList.find((c) => c.id === id);
                return client ? client.name : "";
            })
            .filter((name) => name);

        return names.join(", ");
    };

    const renderAllClientsMenuItems = (selectedIds) => {
        if (clientsLoading) {
            return (
                <MenuItem disabled>
                    <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                        <ListItemText primary="Loading all clients..." style={{ marginLeft: 10 }} />
                    </Box>
                </MenuItem>
            );
        }

        if (!allClientsList || allClientsList.length === 0) {
            return (
                <MenuItem disabled>
                    <ListItemText primary="No clients found" />
                </MenuItem>
            );
        }

        return [
            <MenuItem key="select-all" value="all">
                <Checkbox
                    checked={allClientsList.length > 0 && selectedIds.length === allClientsList.length}
                    indeterminate={
                        selectedIds.length > 0 && selectedIds.length < allClientsList.length
                    }
                />
                <ListItemText primary={`Select All (${allClientsList.length})`} />
            </MenuItem>,
            ...allClientsList.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                    <Checkbox checked={selectedIds.includes(client.id)} />
                    <ListItemText primary={client.name} />
                </MenuItem>
            )),
        ];
    };

    useEffect(() => {
        const loadData = async () => {
            setIsInitialLoading(true);
            try {
                await dispatch(fetchAllClients());
            } finally {
                setIsInitialLoading(false);
            }
        };
        loadData();
    }, [dispatch]);

    // New handler for DB Update client selection from all clients
    const handleDBUpdateClientChange = (event) => {
        const { value } = event.target;

        if (value[value.length - 1] === "all") {
            const allSelected = selectedClientsForDBUpdate.length === allClientsList.length;
            const newValue = allSelected ? [] : allClientsList.map((client) => client.id);
            setSelectedClientsForDBUpdate(newValue);
            return;
        }

        setSelectedClientsForDBUpdate(value);
    };

    const handleBackupUpload = () => {
        if (!formData.backupFiles || formData.backupFiles.length === 0) {
            toast.warning("Please select backup files!");
            return;
        }

        const fd = new FormData();
        Array.from(formData.backupFiles).forEach((file) => {
            fd.append("backupFiles", file);
        });

        dispatch(uploadBackup(fd)).then(() => {
            setFormData((prev) => ({ ...prev, backupFiles: null }));
            setTimeout(() => dispatch(clearUploadData()), 2000);
            toast.success("Backup uploaded successfully!");
        }).catch(() => {
            toast.error("Failed to upload backup!");
        });
    };

    // New function for Notify DB Update
    const handleNotifyDBUpdate = () => {
        if (selectedClientsForDBUpdate.length === 0) {
            toast.warning("Please select at least one client!");
            return;
        }

        const notifyDBData = {
            clientids: selectedClientsForDBUpdate
        };

        dispatch(notifyDBUpdate(notifyDBData))
            .then((action) => {
                if (notifyDBUpdate.fulfilled.match(action)) {
                    toast.success("DB update notification sent successfully!");
                    setSelectedClientsForDBUpdate([]);
                } else {
                    toast.error(action.payload || "Failed to send DB update notification!");
                }
            })
            .catch((error) => {
                toast.error(error.message || "Failed to send DB update notification!");
            });
    };

    const selectedDBClientsCount = selectedClientsForDBUpdate.length;

    // Show loader during initial data loading
    if (isInitialLoading || clientsLoading) {
        return <Loader />;
    }

    return (
        <div className="w-full p-4 space-y-6">
            {/* Notify DB Update Module */}
            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-5">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Notify DB Update
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    {/* Client Select for DB Update */}
                    <div className="md:col-span-8 min-h-[105px]">
                        <FormControl fullWidth size="medium">
                            <InputLabel id="db-notify-client-select-label">
                                Select Clients for DB Update Notification
                            </InputLabel>

                            <Select
                                labelId="db-notify-client-select-label"
                                multiple
                                open={openSelect}
                                value={selectedClientsForDBUpdate}
                                onChange={handleDBUpdateClientChange}
                                onOpen={() => setOpenSelect(true)}
                                onClose={() => setOpenSelect(false)}
                                renderValue={renderClientValue}
                                input={
                                    <OutlinedInput
                                        label="Select Clients for DB Update Notification"
                                        sx={{
                                            backgroundColor: "#F3F8FB",
                                            borderRadius: "8px",
                                            padding: "6px",
                                            "& fieldset": {
                                                borderColor: "black",
                                                borderWidth: "2px",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "black",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "black",
                                            },
                                        }}
                                        endAdornment={
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenSelect((prev) => !prev);
                                                }}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: "8px",
                                                    color: "black",
                                                }}
                                            >
                                                {openSelect ? (
                                                    <FiChevronUp size={20} />
                                                ) : (
                                                    <FiChevronDown size={20} />
                                                )}
                                            </IconButton>
                                        }
                                    />
                                }
                                MenuProps={menuProps}
                                IconComponent={() => null}
                            >
                                {renderAllClientsMenuItems(selectedClientsForDBUpdate)}
                            </Select>
                        </FormControl>

                        {clientsLoading && (
                            <div className="flex items-center mt-2 text-blue-600">
                                <CircularProgress size={16} className="mr-2" />
                                <span className="text-sm">Loading clients...</span>
                            </div>
                        )}

                        {!clientsLoading && (
                            <div className="mt-2 text-gray-600 text-sm">
                                {allClientsList.length} client(s) available •{" "}
                                {selectedDBClientsCount} selected
                            </div>
                        )}
                    </div>

                    {/* Notify DB Update Button */}
                    <div className="md:col-span-2 flex items-center">
                        <button
                            onClick={handleNotifyDBUpdate}
                            disabled={notifyDBUpdateLoading || !selectedClientsForDBUpdate.length}
                            className={`${notifyDBUpdateLoading || !selectedClientsForDBUpdate.length
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-[#122A47] hover:bg-[#0c2545]'
                                } text-white py-2 px-4 rounded-md transition-colors w-full`}
                        >
                            {notifyDBUpdateLoading ? (
                                <span className="flex items-center justify-center">
                                    <CircularProgress size={20} className="mr-2 text-white" />
                                    Sending...
                                </span>
                            ) : (
                                "Notify DB Update"
                            )}
                        </button>
                    </div>

                    {/* Clear Button for DB Update */}
                    <div className="md:col-span-2 flex items-center">
                        <button
                            onClick={() => {
                                setSelectedClientsForDBUpdate([]);
                                toast.info("DB Update selection cleared!");
                            }}
                            className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors w-full"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>

                {/* Badges Section */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2">
                        {selectedDBClientsCount > 0 && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                {selectedDBClientsCount} client{selectedDBClientsCount !== 1 ? "s" : ""} selected for notification
                            </span>
                        )}

                        {formData.backupFiles?.length > 0 && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                {formData.backupFiles.length} backup file
                                {formData.backupFiles.length !== 1 ? "s" : ""}
                            </span>
                        )}

                        {!selectedDBClientsCount && (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                                No clients selected for DB update notification
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {/* Upload Backup Section - WITHOUT Client Selection */}
            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-3">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Upload Backup
                    </h3>
                </div>

                <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                        Upload database backup files (.bak, .sql, .backup) to the server.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="md:col-span-8">
                            <input
                                type="file"
                                multiple
                                accept=".bak,.sql,.backup"
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, backupFiles: e.target.files }))
                                }
                            />
                        </div>
                        <div className="md:col-span-4 flex justify-end">
                            <button
                                onClick={handleBackupUpload}
                                disabled={uploadBackupLoading || !formData.backupFiles}
                                className={`${uploadBackupLoading || !formData.backupFiles
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#122A47] hover:bg-[#0c2545]'
                                    } text-white py-2 px-4 rounded-md transition-colors`}
                            >
                                {uploadBackupLoading ? (
                                    <span className="flex items-center">
                                        <CircularProgress size={20} className="mr-2 text-white" />
                                        Uploading...
                                    </span>
                                ) : (
                                    "Upload Backup"
                                )}
                            </button>
                        </div>
                    </div>

                    {/* File Info */}
                    {formData.backupFiles && formData.backupFiles.length > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <div className="flex items-center text-blue-800">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">
                                    {formData.backupFiles.length} backup file{formData.backupFiles.length !== 1 ? 's' : ''} selected
                                </span>
                            </div>
                            <div className="mt-2 text-sm text-blue-600">
                                <ul className="list-disc pl-5">
                                    {Array.from(formData.backupFiles).slice(0, 3).map((file, index) => (
                                        <li key={index} className="truncate">{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                                    ))}
                                    {formData.backupFiles.length > 3 && (
                                        <li>... and {formData.backupFiles.length - 3} more files</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DbUpdate;